import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchGrants, type GrantRow, type GrantsFilters } from '@/lib/adminApi';

/**
 * `/grants` — the "grants log panel" of ClickUp 869f084w5 §4 (Vladyslav
 * Kolesnyk): who, when, for which platform, how much currency / which
 * subscription / which benefit, including the validity period.
 *
 * WHY IT IS NOT THE AUDIT LOG. `/audit` next door renders the same rows with
 * `payload` collapsed behind a per-row "View" toggle, so four of those five
 * questions need a click each and the fifth (platform) was not recorded at
 * all before this branch. This page is the projection
 * `GET /api/admin/v2/grants` returns — every one of the five is a COLUMN, and
 * the raw payload is still one click away for the rows that carry more.
 *
 * Reversals (a reverted plan, a revoked or expired benefit) are shown, not
 * filtered out: "who took the comp away" is the same question asked backwards,
 * and a panel that only shows grants makes an org's current state unreadable.
 */

const KIND_LABEL: Record<string, string> = {
  credits: 'Credits',
  // Coins taken back by the refund calculator (869f7jkj5) — a negative amount.
  credits_clawback: 'Coins taken back (refund)',
  plan: 'Subscription / pass',
  plan_revert: 'Subscription reverted',
  plan_expired: 'Subscription expired',
  one_time_pass: 'One-time pass',
  one_time_pass_end: 'One-time pass ended',
  benefit: 'Benefit',
  benefit_revoke: 'Benefit revoked',
  other: 'Other',
};

const KIND_OPTIONS = [
  { value: '', label: 'All kinds' },
  { value: 'credits', label: 'Credits' },
  { value: 'credits_clawback', label: 'Coins taken back (refund)' },
  { value: 'plan', label: 'Subscription / pass' },
  { value: 'one_time_pass', label: 'One-time pass' },
  { value: 'benefit', label: 'Benefit' },
  { value: 'plan_revert', label: 'Subscription reverted' },
  { value: 'benefit_revoke', label: 'Benefit revoked' },
];

interface FilterState {
  actor: string;
  target: string;
  platform: string;
  kind: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: FilterState = {
  actor: '',
  target: '',
  platform: '',
  kind: '',
  from: '',
  to: '',
};

function toQuery(filters: FilterState, cursor: string | null): GrantsFilters {
  return {
    actor: filters.actor || undefined,
    target: filters.target || undefined,
    platform: filters.platform || undefined,
    kind: filters.kind || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    cursor: cursor ?? undefined,
  };
}

function formatMoment(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * The "what" column. A grants panel has to say what was given in ONE cell,
 * across three genuinely different things — a number of credits, a plan id, a
 * benefit key — and an empty cell is the one answer that helps nobody, so an
 * unrecognised row falls back to naming its own action.
 */
function whatWasGranted(row: GrantRow): string {
  // A one-time pass's `amount` is a FACTOR, not a credit count — printing
  // "2 credits" for a 2x window would be the most misleading cell on the page.
  if (row.kind === 'one_time_pass' || row.kind === 'one_time_pass_end') {
    const days = typeof row.payload?.days === 'number' ? row.payload.days : null;
    return row.amount !== null && row.amount !== undefined
      ? `${row.amount}x${days ? ` for ${days} days` : ''}`
      : row.action;
  }
  if (row.amount !== null && row.amount !== undefined) return `${row.amount} credits`;
  if (row.plan_id) return `plan ${row.plan_id}`;
  if (row.benefit_key) return row.benefit_key;
  return row.action;
}

export function GrantsPage() {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const cursor = cursorStack[cursorStack.length - 1];

  const { data, isPending, isError } = useQuery({
    queryKey: ['admin-grants', filters, cursor],
    queryFn: () => fetchGrants(toQuery(filters, cursor)),
  });

  function updateFilter<K extends keyof FilterState>(key: K, value: string) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCursorStack([null]);
  }

  const rows = data?.rows ?? [];

  return (
    <div>
      <h1 className="text-h2 font-semibold text-fg-primary">Grants log</h1>
      <p className="mt-1 max-w-3xl text-meta text-fg-secondary">
        Every credit grant, refund, manual subscription/pass activation and benefit grant — and
        every reversal — with who did it, when, for which platform, how much or which one, and for
        how long. Read from the same append-only audit log; nothing here can exist without its
        receipt.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <input
          aria-label="Actor"
          placeholder="Actor email"
          value={filters.actor}
          onChange={(event) => updateFilter('actor', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
        <input
          aria-label="Target"
          placeholder="Organization id"
          value={filters.target}
          onChange={(event) => updateFilter('target', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
        <select
          aria-label="Platform"
          value={filters.platform}
          onChange={(event) => updateFilter('platform', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        >
          <option value="">All platforms</option>
          <option value="WEB">Web</option>
          <option value="APP">App</option>
        </select>
        <select
          aria-label="Kind"
          value={filters.kind}
          onChange={(event) => updateFilter('kind', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        >
          {KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          aria-label="From"
          type="date"
          value={filters.from}
          onChange={(event) => updateFilter('from', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
        <input
          aria-label="To"
          type="date"
          value={filters.to}
          onChange={(event) => updateFilter('to', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
      </div>

      {isError && (
        <p
          role="alert"
          className="mt-4 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
        >
          Could not load the grants log.
        </p>
      )}

      {!isPending && !isError && rows.length === 0 && (
        <p className="mt-8 text-body text-fg-tertiary">No grants match these filters.</p>
      )}

      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-body">
            <thead>
              <tr className="border-b border-border text-meta text-fg-tertiary">
                <th className="py-2 pr-4 font-medium">When</th>
                <th className="py-2 pr-4 font-medium">Who</th>
                <th className="py-2 pr-4 font-medium">Platform</th>
                <th className="py-2 pr-4 font-medium">Kind</th>
                <th className="py-2 pr-4 font-medium">What</th>
                <th className="py-2 pr-4 font-medium">Valid until</th>
                <th className="py-2 pr-4 font-medium">Organization</th>
                <th className="py-2 pr-4 font-medium">Reason</th>
                <th className="py-2 font-medium">Payload</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <GrantTableRow
                  key={row.id}
                  row={row}
                  expanded={expandedId === row.id}
                  onToggle={() => setExpandedId((id) => (id === row.id ? null : row.id))}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          disabled={cursorStack.length <= 1}
          onClick={() => setCursorStack((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack))}
          className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={!data?.next_cursor}
          onClick={() => {
            if (data?.next_cursor) setCursorStack((stack) => [...stack, data.next_cursor]);
          }}
          className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function GrantTableRow({
  row,
  expanded,
  onToggle,
}: {
  row: GrantRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="border-b border-border">
        <td className="py-2 pr-4 text-fg-secondary">{formatMoment(row.created_at)}</td>
        <td className="py-2 pr-4 text-fg-primary">{row.actor_email ?? '—'}</td>
        <td className="py-2 pr-4 text-fg-primary">
          {row.platform === 'APP' ? 'App' : row.platform === 'WEB' ? 'Web' : '—'}
        </td>
        <td className="py-2 pr-4 text-fg-secondary">{KIND_LABEL[row.kind] ?? row.kind}</td>
        <td className="py-2 pr-4 text-fg-primary">{whatWasGranted(row)}</td>
        <td className="py-2 pr-4 text-fg-secondary">
          {row.valid_until ? formatMoment(row.valid_until) : '—'}
        </td>
        <td className="py-2 pr-4 text-fg-secondary">{row.target_id ?? '—'}</td>
        <td className="py-2 pr-4 text-fg-secondary">{row.reason}</td>
        <td className="py-2">
          <button
            type="button"
            onClick={onToggle}
            className="text-meta font-medium text-fg-secondary underline hover:text-fg-primary"
          >
            {expanded ? 'Hide' : 'View'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} className="bg-surface-sunken px-4 py-3">
            <pre className="whitespace-pre-wrap break-all font-mono text-micro text-fg-secondary">
              {JSON.stringify(row.payload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}
